import React, { useCallback, useEffect, useState } from "react";
import Menu from "antd/es/menu";
import _ from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import { MenuDataType } from "../../constants/menu";
import { VuiSidebarProps } from "./interface";
import { canAccess } from "../../utils/helpers";
import { useAuth } from "../../context/auth";
import { useInvoiceCount } from "../../context/invoice";
import { Badge, Space } from "antd";
import { useOverview } from "../../context/overview";

const { SubMenu } = Menu;

const VuiSidebar = ({
  menus,
  mode = "horizontal",
  onChange,
  className = "main-menu-component",
}: VuiSidebarProps) => {
  const { state } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { statusCounts } = useInvoiceCount();
  const { data } = useOverview();
  const [rootSubmenuKeys, setRootSubmenuKeys] = useState([""]);
  const [currentMenu, setCurrentMenu] = useState("");
  const [openKeys, setOpenKeys] = React.useState([""]);

  useEffect(() => {
    const tempSubMenuKeys = _.map(menus, "key");
    setRootSubmenuKeys(tempSubMenuKeys);
    setCurrentMenu(location?.pathname);
  }, [menus, location]);

  const menuHandleClick = (e: any) => {
    setCurrentMenu(e.key);

    navigate(e.key);

    if (onChange) {
      onChange();
    }
  };

  const onOpenChange = (keys: any) => {
    const latestOpenKey = keys.find((key: any) => openKeys.indexOf(key) === -1);

    if (rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  const renderMenu = useCallback(
    (menus: MenuDataType[]) => {
      return menus.map((item) => {
        if (canAccess(item.access, state.user?.permissions as string[])) {
          if (item.children) {
            if (item.type === "group") {
              return (
                <Menu.ItemGroup key={`${item.key}`} title={item.title}>
                  {renderMenu(item.children)}
                </Menu.ItemGroup>
              );
            }

            return (
              <SubMenu
                key={`${item.key}`}
                title={item.title}
                icon={item.icon}
                popupClassName="dropdown-main-menu"
              >
                {renderMenu(item.children)}
              </SubMenu>
            );
          } else {
            // For the Invoice menu, add the separate badges for different status counts
            if (item.key === "/invoice") {
              const hasWaitingApproval = statusCounts.waiting_for_approval > 0;
              const hasScheduled = statusCounts.scheduled > 0;

              if (hasWaitingApproval || hasScheduled) {
                return (
                  <Menu.Item key={`${item.key}`} icon={item.icon}>
                    <Space size={8}>
                      {item.title}
                      <Space size={4}>
                        {hasWaitingApproval && (
                          <Badge
                            count={statusCounts.waiting_for_approval}
                            style={{
                              backgroundColor: "#FAAC14",
                              marginBottom: "2px",
                            }}
                            overflowCount={99}
                            size="small"
                            offset={[0, 0]}
                          />
                        )}
                        {hasScheduled && (
                          <Badge
                            count={statusCounts.scheduled}
                            style={{
                              backgroundColor: "#0078F0",
                              marginBottom: "2px",
                            }}
                            overflowCount={99}
                            size="small"
                            offset={[0, 0]}
                          />
                        )}
                      </Space>
                    </Space>
                  </Menu.Item>
                );
              }
            }

            if (item.key === "/inbox") {
              const totalUnreadInbox = data.total_unread_inbox;

              if (totalUnreadInbox > 0) {
                return (
                  <Menu.Item key={`${item.key}`} icon={item.icon}>
                    <Space size={8}>
                      {item.title}
                      <Space size={4}>
                        <Badge
                          count={totalUnreadInbox}
                          style={{
                            backgroundColor: "#FAAC14",
                            marginBottom: "2px",
                          }}
                          overflowCount={99}
                          size="small"
                          offset={[0, 0]}
                        />
                      </Space>
                    </Space>
                  </Menu.Item>
                );
              }
            }

            return (
              <Menu.Item key={`${item.key}`} icon={item.icon}>
                {item.title}
              </Menu.Item>
            );
          }
        }
      });
    },
    [state.user?.permissions, statusCounts, data],
  );

  return (
    <Menu
      mode={mode}
      onClick={menuHandleClick}
      selectedKeys={[currentMenu]}
      onOpenChange={onOpenChange}
      openKeys={openKeys}
      itemType="group"
      className={className}
    >
      {renderMenu(menus)}
    </Menu>
  );
};

export default VuiSidebar;
